import { useCallback, useState } from 'react';
import { useLocalStorage } from './useLocalStorage';

// Types for OpenRouter API (compatible with OpenAI ChatCompletionMessageParam)
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string | Array<{
    type: 'text' | 'image_url';
    text?: string;
    image_url?: {
      url: string;
    };
  }>;
}

export interface WebSearchPlugin {
  id: 'web';
  engine?: 'native' | 'exa';
  max_results?: number;
  search_prompt?: string;
}

export interface ChatCompletionRequest {
  model: string;
  messages: ChatMessage[];
  stream?: boolean;
  temperature?: number;
  max_tokens?: number;
  plugins?: WebSearchPlugin[];
}

export interface ChatCompletionResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: ChatMessage;
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// Configuration
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1';
const DEFAULT_MODEL = 'x-ai/grok-4-fast';

// Helper function to handle API errors with user-friendly messages
async function handleAPIError(response: Response) {
  if (response.status === 401) {
    throw new Error('Invalid API key. Please check your OpenRouter API key in settings.');
  } else if (response.status === 402) {
    throw new Error('Insufficient credits. Please add credits to your OpenRouter account.');
  } else if (response.status === 400) {
    try {
      const error = await response.json();
      throw new Error(`Invalid request: ${error.error?.message || error.details || error.error || 'Please check your request parameters.'}`);
    } catch {
      throw new Error('Invalid request. Please check your parameters and try again.');
    }
  } else if (response.status === 429) {
    throw new Error('Rate limit exceeded. Please wait a moment and try again.');
  } else if (response.status >= 500) {
    throw new Error('Server error. Please try again in a few moments.');
  } else if (!response.ok) {
    try {
      const errorData = await response.json();
      throw new Error(`API error: ${errorData.error?.message || errorData.details || errorData.error || response.statusText}`);
    } catch {
      throw new Error(`Network error: ${response.statusText}. Please check your connection and try again.`);
    }
  }
}

export function useOpenRouter() {
  const [apiKey, setApiKey] = useLocalStorage<string>('openrouter-api-key', '');
  const [selectedModel, setSelectedModel] = useLocalStorage<string>('openrouter-model', DEFAULT_MODEL);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error helper
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Chat completion function with optional web search
  const sendChatMessage = useCallback(async (
    messages: ChatMessage[],
    model?: string,
    options?: Partial<ChatCompletionRequest>
  ): Promise<ChatCompletionResponse> => {
    if (!apiKey) {
      throw new Error('OpenRouter API key is required. Please add your API key in the settings.');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use :online variant if plugins are specified but model doesn't have it
      let finalModel = model || selectedModel;
      if (options?.plugins && !finalModel.includes(':online')) {
        finalModel = `${finalModel}:online`;
      }

      const requestBody: ChatCompletionRequest = {
        model: finalModel,
        messages,
        ...options
      };

      // Detailed logging
      console.group('🤖 OpenRouter API Request');
      console.log('API Key configured:', apiKey ? '✅ Yes' : '❌ No');
      console.log('Model:', requestBody.model);
      console.log('Web Search:', requestBody.plugins ? '✅ Enabled' : '❌ Disabled');
      console.log('Messages:', messages.length, 'total');
      console.log('Request body:', JSON.stringify(requestBody, null, 2));
      console.groupEnd();

      const response = await fetch(`${OPENROUTER_API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Reddit Prospect AI',
        },
        body: JSON.stringify(requestBody),
      });

      console.group('🌐 OpenRouter API Response');
      console.log('Status:', response.status, response.statusText);
      console.log('OK:', response.ok);
      console.groupEnd();

      await handleAPIError(response);
      const data = await response.json();

      console.group('✅ OpenRouter API Success');
      console.log('Response data:', data);
      console.log('Choices:', data.choices?.length || 0);
      console.log('Usage:', data.usage);

      // Log web search details if available
      if (data.choices?.[0]?.message?.annotations) {
        console.group('🔍 Web Search Results Detected');
        console.log('Annotations:', data.choices[0].message.annotations);
        const urls = data.choices[0].message.annotations
          .filter((a: any) => a.type === 'url_citation')
          .map((a: any) => a.url_citation?.url);
        console.log('URLs Found:', urls);
        console.groupEnd();
      } else {
        console.warn('⚠️ NO WEB SEARCH ANNOTATIONS FOUND - AI may be generating content without web search!');
      }

      // Log the actual content for debugging
      if (data.choices?.[0]?.message?.content) {
        console.group('📝 Response Content Preview');
        const content = data.choices[0].message.content;
        const preview = typeof content === 'string' ? content.substring(0, 500) : JSON.stringify(content).substring(0, 500);
        console.log('First 500 chars:', preview);
        console.groupEnd();
      }

      console.groupEnd();

      return data;
    } catch (err) {
      let errorMessage = 'An unexpected error occurred';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      // Add context for common issues
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network')) {
        errorMessage = 'Network error: Please check your internet connection and try again.';
      }

      console.group('❌ OpenRouter API Error');
      console.error('Error:', errorMessage);
      console.error('Full error:', err);
      console.groupEnd();

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, selectedModel]);

  // Streaming chat completion function
  const sendStreamingMessage = useCallback(async (
    messages: ChatMessage[],
    model: string | undefined,
    onChunk: (chunk: string) => void,
    options?: Partial<ChatCompletionRequest>
  ): Promise<void> => {
    if (!apiKey) {
      throw new Error('OpenRouter API key is required. Please add your API key in the settings.');
    }

    setIsLoading(true);
    setError(null);

    try {
      const requestBody: ChatCompletionRequest = {
        model: model || selectedModel,
        messages,
        stream: true,
        ...options
      };

      const response = await fetch(`${OPENROUTER_API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Reddit Prospect AI',
        },
        body: JSON.stringify(requestBody),
      });

      await handleAPIError(response);

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') return;

              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content;
                if (content) {
                  onChunk(content);
                }
              } catch {
                // Ignore parsing errors for incomplete chunks
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (err) {
      let errorMessage = 'An unexpected error occurred';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      // Add context for common issues
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network')) {
        errorMessage = 'Network error: Please check your internet connection and try again.';
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, selectedModel]);

  return {
    // State
    isLoading,
    error,
    isAuthenticated: !!apiKey,
    apiKey,
    selectedModel,

    // Actions
    sendChatMessage,
    sendStreamingMessage,
    setApiKey,
    setSelectedModel,
    clearError,
  };
}
